import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { User, MessageSquare, Clock, File, MessageCircle, UserMinus, Headphones, UserCheck} from 'react-feather';
import { formatDate } from '../../../../utils/formatDate';
import { toast } from 'react-toastify';
import AddQaList from './addQa';
import { Badge, Button, Card, DescriptionList, EmptyState, Field, Table, TextInput } from '../../../../ui';
import TextArea from './ui-pending/TextArea';
import './qa-list.scss';

import { qaList, updateQaList } from '../../../../Api/apiUtils';
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';

interface QaListProps{
    teacherId:any;
}

type QaField = { key: string; label: string };
type QaSection = { path: string; title: string; detailTitle?: string; icon: React.ReactNode; fields: QaField[] };

/** Scored sections of qaDetails.payload[0], in display order. */
const QA_SECTIONS: QaSection[] = [
  {
    path: 'Opening',
    title: 'Opening',
    icon: <Clock size={18} aria-hidden />,
    fields: [
      { key: 'startedOnTime', label: 'Started on Time' },
      { key: 'internetConnection', label: 'Internet Connection' },
      { key: 'greetingIntroduction', label: 'Greeting/Introduction' },
    ],
  },
  {
    path: 'Feedback',
    title: 'Feedback',
    icon: <MessageSquare size={18} aria-hidden />,
    fields: [
      { key: 'constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass', label: 'Assignment of the Previous Class' },
      { key: 'aOIWithExamples', label: 'AOI with Examples' },
      { key: 'addressTheStudentByTheirName', label: 'Address the Student by Their Name' },
    ],
  },
  {
    path: 'RapportBuilding',
    title: 'Rapport Building',
    detailTitle: 'Reporting Building',
    icon: <File size={18} aria-hidden />,
    fields: [
      { key: 'engagementLevel', label: 'Engagement Level' },
      { key: 'madeTheStudentFeelComfortable', label: 'Made the Student Feel Comfortable' },
    ],
  },
  {
    path: 'communicationSkillsAndSoftskills',
    title: 'Communication Skills and Soft Skills',
    icon: <MessageCircle size={18} aria-hidden />,
    fields: [
      { key: 'deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided', label: 'Dead Air/Fumbling/Fillers/Unnecessary Mute Needs to be Avoided' },
      { key: 'casualApproachToneUsageOfSlangLanguage', label: 'Casual Approach/Tone, Usage of Slang Language' },
      { key: 'acknowledgment', label: 'Acknowledgment' },
      { key: 'activeListening', label: 'Active Listening' },
      { key: 'interruption', label: 'Interruption' },
      { key: 'energyLevel', label: 'Energy Level' },
      { key: 'rateOfSpeech', label: 'Rate of Speech' },
    ],
  },
  {
    path: 'Closing',
    title: 'Closing',
    icon: <UserMinus size={18} aria-hidden />,
    fields: [
      { key: 'customerDelight', label: 'Customer Delight' },
      { key: 'DoubtsSolvingQueryResolution', label: 'Doubts Solving/Query Resolution' },
      { key: 'energeticAndPositiveClosing', label: 'Energetic and Positive Closing' },
      { key: 'disconnectionAfterTheStudent', label: 'Disconnection after the Student' },
    ],
  },
  {
    path: 'TechGadgets',
    title: 'Tech & Gadgets',
    icon: <Headphones size={18} aria-hidden />,
    fields: [
      { key: 'mic', label: 'Mic' },
      { key: 'headphones', label: 'Headphones' },
      { key: 'jacket', label: 'Jacket' },
      { key: 'videoQualityCamAngleBodyLanguageBackground', label: 'Video Quality/Cam Angle/Body Language/Background' },
      { key: 'practiceStudio', label: 'Practice Studio' },
      { key: 'pleaseSpecifyTheInstrumentUsed', label: 'Instrument Used' },
    ],
  },
  {
    path: 'Presentation',
    title: 'Presentation',
    icon: <UserCheck size={18} aria-hidden />,
    fields: [
      { key: 'appropriatelyDressed', label: 'Appropriately Dressed' },
      { key: 'videoOn', label: 'Video On' },
      { key: 'rudeSarcasticIntimidatingBehaviourUnprofessionalism', label: 'Rude/Sarcastic/Intimidating Behaviour/Unprofessionalism' },
    ],
  },
  {
    path: 'Time',
    title: 'Time',
    icon: <Clock size={18} aria-hidden />,
    fields: [{ key: 'sessionAsPerStandardTime', label: 'Session as per Standard Time' }],
  },
];

const SectionTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <span className="tp-qalist__section-title">
    {icon}
    {children}
  </span>
);



const QaList: React.FC<QaListProps> = ({ teacherId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);
  const [data, setData] = useState<any>([]);
  const [isExLoading, setIsExLoading] = useState(false);
  const [excelList,setExcelList] = useState<any[]>([]);
  const [selectedQa, setSelectedQa] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedQa, setEditedQa] = useState<any>(null);
  const [isAddQaModalOpen, setIsAddQaModalOpen] = useState(false);


  useEffect(() => {
    if (teacherId) {
      fetchQaListData(teacherId);
    }
  }, [teacherId]);

  function getEditedValue(editedData: any, id: string, path: string, fallback: any) {
    const keys = path.split(".");
    let value = editedData[id];
    for (let key of keys) {
      if (value && value.hasOwnProperty(key)) {
        value = value[key];
      } else {
        return fallback;
      }
    }
    return value;
  }
  const fetchQaListData = async (teacherId: any) => {
    try {
      const response = await qaList(teacherId);
      // Parse the qaDetails string into an object
      const parsedData = (Array.isArray(response) ? response : [])
        .map((item: any) => ({
          ...item,
          qaDetails: typeof item.qaDetails === "string" ? JSON.parse(item.qaDetails) : item.qaDetails,
        }))
        .filter((item: any) => String(item.qaDetails?.qaType || "").toUpperCase() !== "L&D");
      setExcelList(parsedData);
      setData(parsedData);
      console.log("API Response:", parsedData);
    } catch (error) {
      console.error("Error fetching QA list:", error);
    }
  };

  const handleTeacherClick = (teacher: any) => {
    setSelectedTeacher(teacher);
    setIsModalOpen(true);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const exportToExcel = () => {
    setIsExLoading(true);
  
    // Check if excelList is an array
    if (!Array.isArray(excelList)) {
      setIsExLoading(false);
      console.error("excelList is not an array:", excelList);
      return;
    }
  
    // Function to escape CSV values
    const escapeCSVValue = (value: any) => {
      if (value == null) return ""; // Handle null/undefined
      let strValue = value.toString();
      if (strValue.includes('"') || strValue.includes(',') || strValue.includes('\n') || strValue.includes('\r')) {
        strValue = `"${strValue.replace(/"/g, '""')}"`; // Escape double quotes and wrap in quotes
      }
      return strValue;
    };
  
    // Function to flatten nested objects
    const flattenObject = (obj: any, prefix = '') => {
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          // Recursively flatten nested objects
          Object.assign(acc, flattenObject(obj[key], newKey));
        } else if (Array.isArray(obj[key])) {
          // Handle arrays (e.g., payload)
          obj[key].forEach((item: any, index: number) => {
            Object.assign(acc, flattenObject(item, `${newKey}.${index}`));
          });
        } else {
          // Add non-object and non-array fields
          acc[newKey] = obj[key];
        }
        return acc;
      }, {} as any);
    };
  
    // Flatten the entire excelList data
    const flattenedData = excelList.map((item) => flattenObject(item));
  
    // Extract headers dynamically from the flattened data
    const headers = Array.from(
      new Set(flattenedData.flatMap((item) => Object.keys(item)))
    );
  
    // Convert data to CSV format
    let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n";
  
    csvContent += flattenedData
      .map((item) =>
        headers.map((header) => escapeCSVValue(item[header] || "")).join(",")
      )
      .join("\n");
  
    // Create and trigger the download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "QA_Report.csv");
  
    link.onload = () => setIsExLoading(false);
  
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = (qa: any) => {
    setSelectedQa(qa);
    setEditedQa(JSON.parse(JSON.stringify(qa)));
    setIsEditModalOpen(true);
  };

  // Calculate total of all 0-5 numeric fields within the edited QA payload (ignores qaScore)
  const calculateEditedTotalScore = (): number => {
    try {
      const payloadRoot = editedQa?.qaDetails?.payload?.[0] || {};
      const sections = [
        'Opening',
        'Feedback',
        'RapportBuilding',
        'communicationSkillsAndSoftskills',
        'Closing',
        'TechGadgets',
        'Presentation',
        'Time',
      ];

      let total = 0;

      const addIfValidNumber = (value: any) => {
        if (typeof value === 'number') {
          if (!isNaN(value) && value >= 0 && value <= 5) {
            total += value;
          }
          return;
        }
        if (typeof value === 'string' && value.trim() !== '') {
          const num = parseFloat(value);
          if (!isNaN(num) && num >= 0 && num <= 5) {
            total += num;
          }
        }
      };

      sections.forEach((section) => {
        const sectionObj = payloadRoot?.[section] || {};
        Object.keys(sectionObj).forEach((key) => addIfValidNumber(sectionObj[key]));
      });

      return total;
    } catch {
      return 0;
    }
  };

  const buildPayloadFromEditedQa = (qa: any) => {
    const qaDetails = qa.qaDetails || {};
    const payloadArr = qaDetails.payload || [{}];
    return {
      teacherId: qaDetails.teacherId,
      teacherName: qaDetails.teacherName,
      teacherCategory: qaDetails.teacherCategory,
      studentName: qaDetails.studentName,
      sessionDate: qa.sessionDate || "",
      course: qaDetails.course || "",
      ageGroup: qaDetails.ageGroup || "",
      auditDate: qa.auditDate || "",
      feedbackStatus: qaDetails.feedbackStatus || "",
      feedbackLink: qaDetails.feedbackLink || "",
      observation: qaDetails.observation || "",
      qaScore: qa.qaScore || "0",
      payload: [
        {
          Opening: {
            startedOnTime: payloadArr[0]?.Opening?.startedOnTime || "",
            internetConnection: payloadArr[0]?.Opening?.internetConnection || "",
            greetingIntroduction: payloadArr[0]?.Opening?.greetingIntroduction || "",
          },
          Feedback: {
            constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass: payloadArr[0]?.Feedback?.constructiveFeedbackWhileDiscussingTheAssessmentAssignmentOfThePreviousClass || "",
            aOIWithExamples: payloadArr[0]?.Feedback?.aOIWithExamples || "",
            addressTheStudentByTheirName: payloadArr[0]?.Feedback?.addressTheStudentByTheirName || "",
          },
          RapportBuilding: {
            engagementLevel: payloadArr[0]?.RapportBuilding?.engagementLevel || "",
            madeTheStudentFeelComfortable: payloadArr[0]?.RapportBuilding?.madeTheStudentFeelComfortable || "",
          },
          communicationSkillsAndSoftskills: {
            deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided: payloadArr[0]?.communicationSkillsAndSoftskills?.deadAirFumblingFillersUnnecessaryMuteNeedsToBeAvoided || "",
            casualApproachToneUsageOfSlangLanguage: payloadArr[0]?.communicationSkillsAndSoftskills?.casualApproachToneUsageOfSlangLanguage || "",
            acknowledgment: payloadArr[0]?.communicationSkillsAndSoftskills?.acknowledgment || "",
            activeListening: payloadArr[0]?.communicationSkillsAndSoftskills?.activeListening || "",
            interruption: payloadArr[0]?.communicationSkillsAndSoftskills?.interruption || "",
            energyLevel: payloadArr[0]?.communicationSkillsAndSoftskills?.energyLevel || "",
            rateOfSpeech: payloadArr[0]?.communicationSkillsAndSoftskills?.rateOfSpeech || "",
          },
          Closing: {
            customerDelight: payloadArr[0]?.Closing?.customerDelight || "",
            DoubtsSolvingQueryResolution: payloadArr[0]?.Closing?.DoubtsSolvingQueryResolution || "",
            energeticAndPositiveClosing: payloadArr[0]?.Closing?.energeticAndPositiveClosing || "",
            disconnectionAfterTheStudent: payloadArr[0]?.Closing?.disconnectionAfterTheStudent || "",
          },
          TechGadgets: {
            mic: payloadArr[0]?.TechGadgets?.mic || "",
            headphones: payloadArr[0]?.TechGadgets?.headphones || "",
            jacket: payloadArr[0]?.TechGadgets?.jacket || "",
            videoQualityCamAngleBodyLanguageBackground: payloadArr[0]?.TechGadgets?.videoQualityCamAngleBodyLanguageBackground || "",
            practiceStudio: payloadArr[0]?.TechGadgets?.practiceStudio || "",
            pleaseSpecifyTheInstrumentUsed: payloadArr[0]?.TechGadgets?.pleaseSpecifyTheInstrumentUsed || "",
          },
          Presentation: {
            appropriatelyDressed: payloadArr[0]?.Presentation?.appropriatelyDressed || "",
            videoOn: payloadArr[0]?.Presentation?.videoOn || "",
            rudeSarcasticIntimidatingBehaviourUnprofessionalism: payloadArr[0]?.Presentation?.rudeSarcasticIntimidatingBehaviourUnprofessionalism || "",
          },
          Time: {
            sessionAsPerStandardTime: payloadArr[0]?.Time?.sessionAsPerStandardTime || "",
          },
        },
      ],
    };
  };

  const handleSave = async () => {
    // Prevent save if total numeric values exceed 100
    const totalScore = calculateEditedTotalScore();
    if (totalScore > 100) {
      toast.error(`Total score (${totalScore}) exceeds 100. Please reduce values before saving.`);
      return;
    }
    try {
      const payload = buildPayloadFromEditedQa(editedQa);
      await updateQaList(payload);
      toast.success("QA updated successfully");
      trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_UPDATED, {
        source: "teacher_profile_qa_tab",
        teacherId: teacherId != null ? String(teacherId) : undefined,
        auditId:
          editedQa?.id != null
            ? String(editedQa.id)
            : editedQa?.qaDetails?.teacherId != null
              ? String(editedQa.qaDetails.teacherId)
              : undefined,
      });
      setIsEditModalOpen(false);
      fetchQaListData(teacherId);
    } catch (error) {
      toast.error("Failed to update QA");
      trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_UPDATE_FAILED, {
        source: "teacher_profile_qa_tab",
        teacherId: teacherId != null ? String(teacherId) : undefined,
      });
    }
  };

  const handleInputChange = (path: string, value: any) => {
    setEditedQa((prev: any) => {
      const newQa = { ...prev };
      const keys = path.split(".");
      let obj = newQa;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newQa;
    });
  };

  const handleAddQaSuccess = () => {
    setIsAddQaModalOpen(false);
    fetchQaListData(teacherId);
  };

  const editedTotal = calculateEditedTotalScore();

  return (
    <div className="tp-qalist">
      <div className="tp-qalist__actions">
        <Button variant="secondary" onClick={exportToExcel}>Export to CSV</Button>
        <Button variant="primary" onClick={() => setIsAddQaModalOpen(true)}>
          Add QA
        </Button>
      </div>

      {data.length === 0 ? (
        <EmptyState title="No QA audits yet" description="Use Add QA to record the first audit for this teacher." />
      ) : (
        <Table caption="QA audits">
          <thead>
            <tr>
              <th>Teacher Name</th>
              <th>Teacher Category</th>
              <th>Student Name</th>
              <th>Age Group</th>
              <th>Course</th>
              <th>Session Date</th>
              <th>Audit Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((teacher: any) => (
              <tr key={teacher.id}>
                <td>
                  <Button variant="ghost" size="sm" onClick={() => handleTeacherClick(teacher)}>
                    {teacher.qaDetails.teacherName}
                  </Button>
                </td>
                <td>{teacher.qaDetails.teacherCategory}</td>
                <td>{teacher.qaDetails.studentName}</td>
                <td>{teacher.qaDetails.ageGroup}</td>
                <td>{teacher.qaDetails.course}</td>
                <td>{ formatDate(teacher.sessionDate,"shortDate")}</td>
                <td>{formatDate(teacher.auditDate,"shortDate")}</td>
                <td>
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(teacher)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal to display teacher details */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          Teacher Details: {selectedTeacher?.qaDetails.teacherName}
        </ModalHeader>
        <ModalBody className="tp-qalist__scroll tp-qalist__scroll--details">
          {selectedTeacher && (
            <div className="tp-qalist__sections">
              <Card title={<SectionTitle icon={<User size={18} aria-hidden />}>Basic Information</SectionTitle>}>
                <DescriptionList
                  items={[
                    { label: 'Teacher ID', value: selectedTeacher.qaDetails.teacherId },
                    { label: 'Session Date', value: formatDate(selectedTeacher.sessionDate,"shortDate") },
                    { label: 'Audit Date', value: formatDate(selectedTeacher.auditDate,"shortDate") },
                    { label: 'QA Score', value: selectedTeacher.qaScore },
                    { label: 'Feedback Status', value: selectedTeacher.qaDetails.feedbackStatus },
                    { label: 'Feedback Link', value: selectedTeacher.qaDetails.feedbackLink },
                    { label: 'Observation', value: selectedTeacher.qaDetails.observation },
                  ]}
                />
              </Card>
              {QA_SECTIONS.map((section) => (
                <Card
                  key={section.path}
                  title={<SectionTitle icon={section.icon}>{section.detailTitle ?? section.title}</SectionTitle>}
                >
                  <DescriptionList
                    items={[
                      ...section.fields.map((field) => ({
                        label: field.label,
                        value: selectedTeacher.qaDetails.payload[0][section.path][field.key],
                      })),
                      ...(section.path === 'Time'
                        ? [
                            { label: 'Observation', value: selectedTeacher.qaDetails.observation },
                            { label: 'Qa Score', value: selectedTeacher.qaScore },
                          ]
                        : []),
                    ]}
                  />
                </Card>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal for editing */}
      <Modal size='xl' isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(false)}>
        <ModalHeader toggle={() => setIsEditModalOpen(false)} >Edit QA</ModalHeader>
        <ModalBody>
          <div className="tp-qalist__scroll tp-qalist__sections">
            <Card title={<SectionTitle icon={<User size={18} aria-hidden />}>Basic Information</SectionTitle>}>
              <div className="tp-qalist__grid">
                <Field label="Feedback Status">
                  {(p) => (
                    <TextInput
                      {...p}
                      type="text"
                      value={editedQa?.qaDetails?.feedbackStatus || ""}
                      onChange={e => handleInputChange("qaDetails.feedbackStatus", e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Feedback Link">
                  {(p) => (
                    <TextInput
                      {...p}
                      type="text"
                      value={editedQa?.qaDetails?.feedbackLink || ""}
                      onChange={(e) => handleInputChange("qaDetails.feedbackLink", e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Observation" className="tp-qalist__full">
                  {(p) => (
                    <TextArea
                      {...p}
                      value={editedQa?.qaDetails?.observation || ""}
                      onChange={(e) => handleInputChange("qaDetails.observation", e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Card>

            {QA_SECTIONS.map((section) => (
              <Card key={section.path} title={<SectionTitle icon={section.icon}>{section.title}</SectionTitle>}>
                <div className="tp-qalist__grid">
                  {section.fields.map((field) => (
                    <Field key={field.key} label={field.label}>
                      {(p) => (
                        <TextInput
                          {...p}
                          type="text"
                          value={editedQa?.qaDetails?.payload?.[0]?.[section.path]?.[field.key] || ""}
                          onChange={(e) => handleInputChange(`qaDetails.payload.0.${section.path}.${field.key}`, e.target.value)}
                        />
                      )}
                    </Field>
                  ))}
                  {section.path === 'Time' && (
                    <Field label="QA Score">
                      {(p) => (
                        <TextInput
                          {...p}
                          type="text"
                          value={editedQa?.qaScore || ""}
                          onChange={(e) => handleInputChange("qaScore", e.target.value)}
                        />
                      )}
                    </Field>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </ModalBody>
        <ModalFooter className="tp-qalist__footer">
          {/* Live total score (out of 100) and warning if exceeding */}
          <div className="tp-qalist__total" aria-live="polite">
            <Badge tone={editedTotal > 100 ? 'danger' : 'success'}>Total Score: {editedTotal}/100</Badge>
            {editedTotal > 100 && <span className="tp-qalist__over">Exceeds 100</span>}
          </div>
          <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </ModalFooter>
      </Modal>

      <Modal size='xl' isOpen={isAddQaModalOpen} toggle={() => setIsAddQaModalOpen(false)}>
        <ModalHeader toggle={() => setIsAddQaModalOpen(false)}>
          Add QA
        </ModalHeader>
        <ModalBody>
          <AddQaList
            teacherName={data[0]?.qaDetails?.teacherName || ""}
            teacherId={data[0]?.qaDetails?.teacherId || teacherId}
            fetchQaListData ={fetchQaListData}
            onSuccess={handleAddQaSuccess}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default QaList;
